import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import {
  LogIn,
  AlertCircle,
  Copy,
  Check,
  ExternalLink,
  ChevronDown,
  MessageSquare,
  Info,
  AlertTriangle,
  XCircle,
  User,
  Clock,
  Tag
} from 'lucide-react'
import { publicApi } from '../api/events'
import { copyToClipboard } from '../utils/clipboard'
import type { SharedEvent } from '../types'
import { useAuth } from '../context/AuthContext'

const levelColors: Record<string, string> = {
  Verbose: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  Debug: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  Information: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  Warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  Error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  Fatal: 'bg-red-200 text-red-900 font-bold dark:bg-red-900 dark:text-red-200',
}

const levelBgColors: Record<string, string> = {
  Verbose: 'bg-gray-400',
  Debug: 'bg-gray-500',
  Information: 'bg-blue-500',
  Warning: 'bg-yellow-500',
  Error: 'bg-red-500',
  Fatal: 'bg-red-700',
}

const annotationSeverityConfig: Record<string, { icon: React.ReactNode; bg: string; border: string; text: string }> = {
  Info: {
    icon: <Info className="w-4 h-4" />,
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-400'
  },
  Notice: {
    icon: <AlertCircle className="w-4 h-4" />,
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400'
  },
  Warning: {
    icon: <AlertTriangle className="w-4 h-4" />,
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400'
  },
  Critical: {
    icon: <XCircle className="w-4 h-4" />,
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-400'
  }
}

function formatTimeAgo(date: string): string {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

export function PublicEventView() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [event, setEvent] = useState<SharedEvent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    properties: true,
    exception: true,
    annotations: true,
  })

  useEffect(() => {
    // If user is authenticated, redirect to events page with the event ID
    if (user && event) {
      navigate(`/events?eventId=${event.id}`, { replace: true })
    }
  }, [user, event, navigate])

  useEffect(() => {
    const fetchEvent = async () => {
      if (!token) {
        setError(t('share.invalidToken'))
        setLoading(false)
        return
      }

      try {
        const data = await publicApi.getSharedEvent(token)
        setEvent(data)
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        if (errorMessage.includes('404') || errorMessage.includes('not found')) {
          setError(t('share.eventNotFound'))
        } else {
          setError(t('share.loadError'))
        }
      } finally {
        setLoading(false)
      }
    }

    fetchEvent()
  }, [token, t])

  const handleCopyJson = async () => {
    if (!event) return
    const success = await copyToClipboard(JSON.stringify(event, null, 2))
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/pcilogs-logo-200px.png" alt="Vigia" className="h-8 w-auto" />
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                Vigia
              </span>
            </div>
          </div>
        </header>

        {/* Error state */}
        <main className="max-w-4xl mx-auto p-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
            <AlertCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {error || t('share.eventNotFound')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('share.errorDescription')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <LogIn size={18} />
              {t('share.loginToExplore')}
            </Link>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/pcilogs-logo-200px.png" alt="Vigia" className="h-8 w-auto" />
            <span className="text-lg font-semibold text-gray-900 dark:text-white">
              Vigia
            </span>
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
              {t('share.sharedEvent')}
            </span>
          </div>
          <Link
            to="/login"
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <LogIn size={16} />
            {t('common.login')}
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto p-4 lg:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          {/* Event header */}
          <div className="flex items-start gap-3 p-4 lg:p-6 border-b dark:border-gray-700">
            <div className={`w-1.5 self-stretch rounded-sm ${levelBgColors[event.level] || 'bg-gray-400'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${levelColors[event.level] || levelColors.Information}`}>
                  {event.level}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                  {format(new Date(event.timestamp), 'yyyy-MM-dd HH:mm:ss.SSS')}
                </span>
                {event.application && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    · {event.application}
                  </span>
                )}
              </div>
              <p className="text-gray-900 dark:text-gray-100 font-mono text-sm lg:text-base break-words">
                {event.message}
              </p>
            </div>
          </div>

          {/* Event details */}
          <div className="p-4 lg:p-6 space-y-4">
            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleCopyJson}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  copied
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? t('common.copied') : t('events.detail.exportJson')}
              </button>
              <Link
                to={`/login?returnUrl=/events?eventId=${event.id}`}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
              >
                <ExternalLink size={16} />
                {t('share.viewInApp')}
              </Link>
            </div>

            {/* Annotations */}
            {event.annotations && event.annotations.length > 0 && (
              <div className="border border-amber-500/30 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('annotations')}
                  className="w-full flex items-center justify-between px-4 py-2 bg-amber-500/10"
                >
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-medium text-amber-300">
                      {t('annotations.title')} ({event.annotations.length})
                    </span>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`text-amber-400 transition-transform ${expandedSections.annotations ? 'rotate-180' : ''}`}
                  />
                </button>
                {expandedSections.annotations && (
                  <div className="p-4 space-y-3 bg-gray-800/30">
                    {event.annotations.map((annotation) => {
                      const severity = annotationSeverityConfig[annotation.severity] || annotationSeverityConfig.Info
                      const customColor = annotation.color
                      const borderStyle = customColor
                        ? { borderLeftColor: customColor, borderLeftWidth: '4px' }
                        : undefined
                      const bgClass = customColor ? 'bg-gray-800/50' : severity.bg
                      const borderClass = customColor ? 'border-gray-700' : severity.border

                      return (
                        <div
                          key={annotation.id}
                          className={`rounded-lg border ${borderClass} ${bgClass} p-3 space-y-2`}
                          style={borderStyle}
                        >
                          {/* Header */}
                          <div className="flex items-center gap-2">
                            <span className={severity.text}>{severity.icon}</span>
                            <h4 className="font-medium text-gray-100 text-sm">{annotation.title}</h4>
                          </div>

                          {/* Content */}
                          {annotation.content && (
                            <p className="text-sm text-gray-300 whitespace-pre-wrap">{annotation.content}</p>
                          )}

                          {/* Tags */}
                          {annotation.tags.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              <Tag className="w-3 h-3 text-gray-500" />
                              {annotation.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="px-1.5 py-0.5 text-xs bg-gray-700/50 text-gray-300 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Footer */}
                          <div className="flex items-center gap-3 text-xs text-gray-500 pt-1 border-t border-gray-700/50">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {annotation.ownerUsername}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTimeAgo(annotation.createdAt)}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Exception */}
            {event.exception && (
              <div className="border dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('exception')}
                  className="w-full flex items-center justify-between px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300"
                >
                  <span className="text-sm font-medium">{t('events.detail.exception')}</span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${expandedSections.exception ? 'rotate-180' : ''}`}
                  />
                </button>
                {expandedSections.exception && (
                  <pre className="p-4 text-xs text-red-600 dark:text-red-400 bg-red-50/50 dark:bg-red-900/10 overflow-auto max-h-64">
                    {event.exception}
                  </pre>
                )}
              </div>
            )}

            {/* Properties */}
            {Object.keys(event.properties).length > 0 && (
              <div className="border dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('properties')}
                  className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300"
                >
                  <span className="text-sm font-medium">{t('events.detail.properties')}</span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${expandedSections.properties ? 'rotate-180' : ''}`}
                  />
                </button>
                {expandedSections.properties && (
                  <pre className="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50/50 dark:bg-gray-800/50 overflow-auto max-h-64">
                    {JSON.stringify(event.properties, null, 2)}
                  </pre>
                )}
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">
                  {t('events.detail.metadata.eventId')}
                </span>
                <p className="font-mono text-xs text-gray-900 dark:text-gray-200 truncate mt-1">
                  {event.id}
                </p>
              </div>
              {event.traceId && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">
                    {t('events.detail.metadata.traceId')}
                  </span>
                  <p className="font-mono text-xs text-gray-900 dark:text-gray-200 truncate mt-1">
                    {event.traceId}
                  </p>
                </div>
              )}
              {event.environment && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">
                    {t('events.detail.metadata.environment')}
                  </span>
                  <p className="text-gray-900 dark:text-gray-200 mt-1">
                    {event.environment}
                  </p>
                </div>
              )}
              {event.machineName && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">
                    {t('events.detail.metadata.machine')}
                  </span>
                  <p className="text-gray-900 dark:text-gray-200 mt-1">
                    {event.machineName}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            {t('share.ctaText')}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <LogIn size={18} />
            {t('share.loginToExplore')}
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        <p>
          {t('share.developedBy')}{' '}
          <a
            href="https://proconsi.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Proconsi
          </a>
        </p>
      </footer>
    </div>
  )
}
